import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, Input, ViewChild, TemplateRef, EventEmitter, Output, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { IModelContext } from '../../models/IModelContext';
import { IPanelContext } from '../../models/IPanelContext';
import { FormGroup, FormBuilder, FormArray, FormControl, FormGroupDirective } from '@angular/forms';
import { IListPanelContext } from '../../models/IListPanelContext';
import { IMetadataEvent } from '../../models/IMetadataEvent';
import { PageRequestModel } from '../../../../core/models/PageRequestModel';
import { IMetadataWorkspaceContext } from '../../models/IMetadataWorkspaceContext';
import { IListSelector } from '../../models/IListSelector';
import { IMetadataProvider } from '../../services/IMetadataProvider';
import { MetadataTemplateManagerService } from '../../services/metadata-template-manager.service';
import { Store } from '@ngrx/store';
import { AppliedRuleViolation } from './AppliedRuleViolationModel';
import { Subject } from 'rxjs';
import { RuleVioliationManagerService } from '../../services/rule-violiation-manager.service';
import { v4 as uuid } from 'uuid';
import { MatDatepickerInputEvent } from '@angular/material';
import * as moment from 'moment';


@Component({
  selector: 'plr-metadata-workspace',
  templateUrl: './metadata-workspace.component.html',
  styleUrls: ['./metadata-workspace.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MetadataWorkspaceComponent implements OnInit, OnDestroy, AfterViewInit {
  panelContexts: IPanelContext[] = [];
  _context: IMetadataWorkspaceContext;
  _metadata: any;
  _model: any;
  workspaceForm: FormGroup;
  _activeSubEntityWorkspace: IMetadataWorkspaceContext;
  _subEntityWorkspaces: IMetadataWorkspaceContext[];
  _initialized = false;
  _appliedRuleViolations: AppliedRuleViolation[] = [];
  unsubscribeAll$: Subject<boolean> = new Subject<boolean>();

  @ViewChild(FormGroupDirective)
  FormDirective: FormGroupDirective;

  @ViewChild('standard')
  standardTemplate: TemplateRef<any>;

  @ViewChild('list')
  listTemplate: TemplateRef<any>;

  @ViewChild('listTile')
  listTileTemplate: TemplateRef<any>;

  @ViewChild(MetadataWorkspaceComponent)
  subEntityWorkspace: MetadataWorkspaceComponent;

  @Input()
  set context(context: IMetadataWorkspaceContext) {
    if (this._context != context) {
      this._context = context;
      if (this._metadata !== context.metadata || this._model !== context.model) {
        this._metadata = context.metadata;
        this._model = context.model;
        if (this._initialized) {
          this.generateIPanelContexts();
        }
      }
    }
  }

  @Input()
  metadataProvider: IMetadataProvider;

  @Input()
  listSelectorFactory: (entityKey: string, scope: string) => IListSelector;

  @Input()
  set subEntityWorkspaces(subEntityWorkspaces: IMetadataWorkspaceContext[]) {
    this._subEntityWorkspaces = subEntityWorkspaces;
    if (this._subEntityWorkspaces && this._subEntityWorkspaces.length > 0) {
      this._activeSubEntityWorkspace = this._subEntityWorkspaces[this._subEntityWorkspaces.length - 1];
      if (!this._activeSubEntityWorkspace.formGroup) {
        this.setWorkspaceContextFormGroup(this._activeSubEntityWorkspace);
      }
    } else {
      this._activeSubEntityWorkspace = undefined;
    }
  }

  @Input()
  defaultGroupName: string = 'Unknown';

  @Output()
  pageRequested = new EventEmitter<IMetadataEvent<PageRequestModel>>();

  @Output()
  subEntitySelected = new EventEmitter<IMetadataEvent<any>>();

  @Output()
  subEntityAdded = new EventEmitter<IMetadataEvent<any>>();

  @Output()
  subEntityCloned = new EventEmitter<IMetadataEvent<any>>();

  @Output()
  subEntityAssociated = new EventEmitter<IMetadataEvent<any>>();

  constructor(
    private formBuilder: FormBuilder,
    private metadataTemplateManager: MetadataTemplateManagerService,
    private store: Store<any>,
    public ruleViolationManager: RuleVioliationManagerService,
    private _changeDetector: ChangeDetectorRef,
  ) {}

  ngOnDestroy() {
    this.unsubscribeAll$.next(true);
  }

  ngOnInit() {
    this.generateIPanelContexts();
    this._initialized = true;
  }

  ngAfterViewInit(): void {}

  private generateIPanelContexts() {
    this.panelContexts = [];
    this.unsubscribeAll$.next(true);
    this.unsubscribeAll$.next(false);
    if (this._model && this._metadata) {
      var panelContextMap = new Map<string, IPanelContext>();
      this.workspaceForm = this._context.formGroup || this.createDefaultFormGroup('root', this._metadata.classMetadataItems);
      if (!this._context.formGroup) {
        this._context.formGroup = this.workspaceForm;
      }
      var newForm = false;
      if (!this._model.key) {
        this.workspaceForm.markAsDirty();
        newForm = true;
      }
      this.processModel(this._model, this._metadata, '', panelContextMap, this.workspaceForm);
      if ((this.workspaceForm.valid && !this.workspaceForm.dirty) || newForm) {
        this.workspaceForm.patchValue(this._model);
      }

      panelContextMap.forEach((m) => {
        if (m.fieldContexts) {
          m.fieldContexts = m.fieldContexts.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
        }
        this.panelContexts.push(m);
      });
    }
    this.panelContexts = this.panelContexts.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  }

  createAssociationPanelContexts(associationContext: IMetadataWorkspaceContext) {
    this.panelContexts = [];
    this.unsubscribeAll$.next(true);
    this.unsubscribeAll$.next(false);
    if (associationContext.model && associationContext.metadata) {
      var panelContextMap = new Map<string, IPanelContext>();
      var workspaceForm = associationContext.formGroup || this.createDefaultFormGroup(associationContext.scope, associationContext.metadata.classMetadataItems);
      if (!associationContext.formGroup) {
        associationContext.formGroup = workspaceForm;
      }
      this.processModel(associationContext.model, associationContext.metadata, '', panelContextMap, associationContext.formGroup, '', true);
      
      panelContextMap.forEach((m) => {
        if (m.fieldContexts) {
          m.fieldContexts = m.fieldContexts.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
        }
        this.panelContexts.push(m);
      });
    }
    this.panelContexts = this.panelContexts.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    this.setWorkspaceContextFormGroup(associationContext);
  }

  private createDefaultFormGroup(name: string, metadata: any): FormGroup {
    var formGroup = this.formBuilder.group({
      $type: [metadata.$type],
    });
    (formGroup as any).nameIdentifier = name;
    this.ruleViolationManager.setFormGroupValidators(formGroup);
    return formGroup;
  }

  private createDefaultFormControl(formGroup: FormGroup, property: string, metadata: any): FormControl {
    var control = formGroup.controls[property];
    if (!control) {
      control = this.formBuilder.control(undefined);
      formGroup.addControl(property, control);
    }
    if (metadata.parentDataLinkOnOtherEntity == true) {
      if (formGroup.controls[metadata.parentProperty]) {
        formGroup.controls[metadata.parentProperty].setValue(formGroup.root.value[metadata.parentProperty]);
      }
    }
    this.ruleViolationManager.registerControl(control);
    if (metadata.readOnly) {
      control.disable();
    }
    return control as FormControl;
  }

  private processModel(model: any, metadata: any, path: string, panelContextMap: Map<string, IPanelContext>, formGroup: FormGroup, groupNamePrefix: string = '', isAssociation: boolean = false) {
    for (var property in metadata.propertyMetadataItems) {
      if (metadata.propertyMetadataItems.hasOwnProperty(property)) {
        var propertyMetadata = metadata.propertyMetadataItems[property];
        if (this.shouldIgnore(propertyMetadata)) {
          continue;
        }
        var currentPath = path ? `${path}.${property}` : property;
        var groupName = propertyMetadata.groupName || this.defaultGroupName;
        if (groupNamePrefix) {
          if (groupName === 'Profile') {
            groupName = groupNamePrefix;
          } else {
            groupName = `${groupNamePrefix} - ${groupName}`;
          }
        }
        var panelContext: IPanelContext = panelContextMap.get(groupName);

        if (!panelContextMap.has(groupName)) {
          panelContext = {
            identifier: new Date().getTime().toString(),
            displayName: groupName,
            displayOrder: 0,
            fieldContexts: [],
            template: this.standardTemplate,
            formGroup: formGroup,
            objectType: metadata.classMetadataItems.$type,
            path: path,
            hidden: propertyMetadata.hidden ? propertyMetadata.hidden : false,
          };

          panelContextMap.set(groupName, panelContext);
        }
        if (this.isValueType(propertyMetadata)) {
          var fieldContext = undefined;
          fieldContext = this.createFieldContext(model, metadata, property, propertyMetadata, currentPath);
          panelContext.fieldContexts.push(fieldContext);
          if (panelContext.fieldContexts.every((f) => f.hidden === true)) {
            panelContext.hidden = true;
          } else panelContext.hidden = false;

          var control = this.createDefaultFormControl(formGroup, property, propertyMetadata);
          if (fieldContext) {
            fieldContext.control = control;
            // Mark all controls dirty if cloned
            if(this._context.isClone == true){
              fieldContext.control.markAsDirty();
              if(fieldContext.metadata.doNotCopyOnClone === true){
                model[property] = null;
              }
            }
            if(isAssociation){
              fieldContext.control.markAsDirty();
            }
          }
        } else if (this.isArray(propertyMetadata)) {
          if (propertyMetadata.hidden === true) {
            continue;
          }
          var currentModel = model[property] || [];
          let arrayControl: FormArray = formGroup.get(property) as FormArray;
          if (!arrayControl) {
            arrayControl = this.formBuilder.array([]);
            formGroup.addControl(property, arrayControl);
          }
          var listPanelContext: IListPanelContext = {
            ...panelContext,
            displayOrder: propertyMetadata.displayOrder || 1,
            template: propertyMetadata.simpleList === true ? this.listTemplate : this.listTileTemplate,
            itemContexts: [],
            rootModel: this._model,
            rootModelMetadata: this._metadata,
            model: model,
            modelMetadata: metadata,
            path: currentPath,
            property: property,
            metadata: propertyMetadata,
            listMetadata: this.metadataProvider.get(propertyMetadata.$type),
            listSelectors: this.listSelectorFactory(propertyMetadata.entityKey, property),
            hidden: propertyMetadata.hidden,
            formArray: arrayControl,
          };
          if (listPanelContext.listMetadata) {
            listPanelContext.listMetadata.displayName = propertyMetadata.displayName;
          }
          panelContextMap.set(groupName, listPanelContext);

          currentModel.forEach((m, i) => this.addArrayFormGroup(m, i, listPanelContext, formGroup));
          if (propertyMetadata.simpleList === true) {
            const formArray = formGroup.get(property) as FormArray;
            if (formArray.controls.length > listPanelContext.itemContexts.length) {
              for (let i = listPanelContext.itemContexts.length; i < formArray.controls.length; i++) {
                this.addArrayFormGroup(null, i, listPanelContext, formGroup);
              }
            }
          }
        } else if (this.isObject(propertyMetadata)) {
          var currentModel = model[property] || {};
          var subFormGroup = formGroup.controls[property] as FormGroup;
          var subMetadata = this.metadataProvider.get(propertyMetadata.$type);
          if (!formGroup.controls.hasOwnProperty(property)) {
            subFormGroup = this.createDefaultFormGroup(property, subMetadata.classMetadataItems);
            formGroup.addControl(property, subFormGroup);
          }
          panelContext.formGroup = subFormGroup;
          this.processModel(currentModel, subMetadata, currentPath, panelContextMap, subFormGroup, propertyMetadata.groupName);
        }
      }
    }
  }

  undoChange(fieldContext: IModelContext) {
    if (fieldContext.control) {
      fieldContext.control.patchValue(fieldContext.model[fieldContext.property]);
      fieldContext.control.markAsPristine();
    }
  }

  delete(panelContext: IListPanelContext, index: number, item: any) {
    var formArray = panelContext.formGroup.get(panelContext.property) as FormArray;
    var formGroup = this.GetFormGroupForDeleteOperation(panelContext, index, item);
    if (formGroup) {
      if (formGroup.value.key) {
        if (panelContext.metadata.simpleList === true) {
          if (panelContext.model[panelContext.property].length > index) {
            formGroup.patchValue(panelContext.model[panelContext.property][index]);
          }
        } else {
          if (this._subEntityWorkspaces) {
            var workspaceContext = this._subEntityWorkspaces.find((w) => w.model.key === formGroup.value.key);
            if (workspaceContext) {
              formGroup.patchValue(workspaceContext.model);
            }
          }
        }
        var control = formGroup.get('UI_action');
        if (!control) {
          control = this.formBuilder.control('delete');
          formGroup.addControl('UI_action', control);
          control.markAsDirty();
        } else {
          control.markAsDirty();
          control.patchValue('delete');
        }
        setTimeout(() => {
          control.markAsDirty();
          formGroup.markAsDirty();
        });
      } else {
        formArray.removeAt(index);
        if (panelContext.metadata.simpleList === true) {
          panelContext.itemContexts = [...panelContext.itemContexts.slice(0, index), ...panelContext.itemContexts.slice(index + 1)];
        }
      }
      Object.keys(formGroup.controls).forEach((key) => {
        this.ruleViolationManager.clearRuleViolations(formGroup.get(key));
      });
    }
  }

  undoDelete(panelContext: IListPanelContext, index: number, item: any) {
    var formArray = panelContext.formGroup.get(panelContext.property) as FormArray;
    var formGroup = this.GetFormGroupForDeleteOperation(panelContext, index, item);
    if (formGroup) {
      if (panelContext.metadata.simpleList === true) {
        if (panelContext.model[panelContext.property].length > index) {
          formGroup.patchValue(panelContext.model[panelContext.property][index]);
        }
      } else {
        if (this._subEntityWorkspaces != null && this._subEntityWorkspaces) {
          var workspaceContext = this._subEntityWorkspaces.find((w) => w.model.key === formGroup.value.key);
          if (workspaceContext) {
            formGroup.patchValue(workspaceContext.model);
          }
        }
      }
      formGroup.markAsPristine();
      var model = undefined;
      if (panelContext.metadata.simpleList === true) {
        model = panelContext.model[panelContext.property][index];
      } else {
        if (this._subEntityWorkspaces != null && this._subEntityWorkspaces) {
          var workspaceContext = this._subEntityWorkspaces.find((w) => w.model.key === formGroup.value.key);
          if (workspaceContext) {
            model = workspaceContext.model;
          } else {
            model = item;
          }
        } else {
          model = item;
        }
      }
      var control = formGroup.get('UI_action');
      if (!control) {
        const actionControl = this.formBuilder.control('undo-delete');
        formGroup.addControl('UI_action', actionControl);
        actionControl.markAsDirty();
      } else {
        if (model && model.isEntityDeleted) {
          control.patchValue('undo-delete');
        } else if (panelContext.metadata.simpleList !== true) {
          formArray.removeAt(index);
        } else {
          control.patchValue('');
        }
      }
    }
  }

  getValueForDisplay(fieldContext: IModelContext): string {
    var value = fieldContext.model[fieldContext.property];
    if (value === null || value === undefined) {
      return '<blank>';
    }
    return value.displayName || value.description || value.toString() || value;
  }

  addArrayFormGroup(model, index, listPanelContext: IListPanelContext, parentFormGroup?: FormGroup) {
    if (!parentFormGroup) {
      parentFormGroup = this.workspaceForm;
    }
    const formArray = parentFormGroup.get(listPanelContext.property) as FormArray;
    let formGroup = index < formArray.controls.length && index >= 0 ? (formArray.controls[index] as FormGroup) : undefined;
    if (!formGroup) {      
      formGroup = this.createDefaultFormGroup(listPanelContext.property, listPanelContext.listMetadata.classMetadataItems);      
      formGroup.addControl('UI_INDEX', this.formBuilder.control(formArray.length));
      formArray.push(formGroup);
    } else {
      if (!formGroup.get('UI_INDEX') || formGroup.get('UI_INDEX').value !== index) {
        formGroup.addControl('UI_INDEX', this.formBuilder.control(index));
      }
    }
    const panelContextMap = new Map<string, IPanelContext>();
    let modelToProcess = model;
    if (modelToProcess && modelToProcess.$type !== listPanelContext.metadata.$type) {
      const modelMetadata = this.metadataProvider.get(modelToProcess.$type);
      for (const property in modelMetadata.propertyMetadataItems) {
        if (modelMetadata.propertyMetadataItems.hasOwnProperty(property)) {
          const propertyMetadata = modelMetadata.propertyMetadataItems[property];
          if (propertyMetadata.entityType && propertyMetadata.entityType.toLowerCase() === listPanelContext.metadata.$type.toLowerCase()) {
            modelToProcess = modelToProcess[property];
            break;
          }
        }
      }
    }
    this.processModel(modelToProcess || {}, listPanelContext.listMetadata, '', panelContextMap, formGroup);
    let context = panelContextMap.get('Profile');
    if (!context) {
      context = panelContextMap.get('Unknown');
    }
    if (context) {
      listPanelContext.itemContexts.push(context.fieldContexts.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)));
    }
  }

  onSubEntityPageRequest(request: IMetadataEvent<PageRequestModel>) {
    this.pageRequested.emit(request);
  }

  onSubEntitySelected(event: IMetadataEvent<any>) {
    this.subEntitySelected.emit(event);
  }

  onSubEntityAdded(event: IMetadataEvent<any>) {
    this.subEntityAdded.emit(event);
  }

  onSubEntityCloned(event: IMetadataEvent<any>) {
    this.subEntityCloned.emit(event);
  }

  onSubEntityAssociated(event: IMetadataEvent<any>){
    this.subEntityAssociated.emit(event);
  }

  onListPageRequest(panelContext: IListPanelContext, request: PageRequestModel) {
    var outerRequest = {
      metadata: panelContext.metadata,
      parentEntityKey: this._metadata.classMetadataItems.entityKey,
      parentKey: this._model.key,
      scope: panelContext.property,
      data: request,
      showDeleted: panelContext.showingDeleted,
    };
    this.pageRequested.emit(outerRequest);
  }

  onShowDeletedPageRequest(panelContext: IListPanelContext, request: PageRequestModel) {
    panelContext.showingDeleted = true;
    var outerRequest = {
      metadata: panelContext.metadata,
      parentEntityKey: this._metadata.classMetadataItems.entityKey,
      parentKey: this._model.key,
      scope: panelContext.property,
      data: request,
      showDeleted: panelContext.showingDeleted,
    };
    this.pageRequested.emit(outerRequest);
  }

  onDontShowDeletedPageRequest(panelContext: IListPanelContext, request: PageRequestModel) {
    panelContext.showingDeleted = false;
    var outerRequest = {
      metadata: panelContext.metadata,
      parentEntityKey: this._metadata.classMetadataItems.entityKey,
      parentKey: this._model.key,
      scope: panelContext.property,
      data: request,
      showDeleted: panelContext.showingDeleted,
    };
    this.pageRequested.emit(outerRequest);
  }

  onListItemSelected(panelContext: IListPanelContext, item: any) {
    this.subEntitySelected.emit({
      metadata: panelContext.metadata,
      scope: panelContext.property,
      data: item,
    });
  }

  onListItemAdded(panelContext: IListPanelContext, item: any) {
    this.subEntityAdded.emit({
      metadata: panelContext.metadata,
      parentEntityKey: this._metadata.classMetadataItems.entityKey,
      parentKey: this._model.key,
      scope: panelContext.property,
      data: item,
    });
  }

  onListItemCloned(panelContext: IListPanelContext, item: any) {
    this.subEntityCloned.emit({
      metadata: panelContext.metadata,
      scope: panelContext.property,
      data: item,
    });
  }

  onListItemAssociated(panelContext: IListPanelContext, items: any) {
    this.subEntityAssociated.emit({
      metadata: panelContext.metadata,
      parentEntityKey: this._metadata.classMetadataItems.entityKey,
      parentKey: this._model.key,
      scope: panelContext.property,
      data: items,
    });
  }

  setWorkspaceContextFormGroup(context: IMetadataWorkspaceContext) {
    var panelContext: IListPanelContext = undefined;
    if (this.subEntityWorkspace) {
      panelContext = this.subEntityWorkspace.panelContexts.find((c: IListPanelContext) => c.property === context.scope) as IListPanelContext;
    }
    if (!panelContext) {
      panelContext = this.panelContexts.find((c: IListPanelContext) => c.property === context.scope) as IListPanelContext;
    }
    if (panelContext) {
      var formArray = panelContext.formGroup.get(panelContext.property) as FormArray;
      var index = formArray.length;
      var formGroup: FormGroup;
      if (context.model.key) {
        formGroup = formArray.controls.find((fg) => fg.value.key === context.model.key) as FormGroup;
        if(formGroup){
          index = formArray.controls.indexOf(formGroup);
        }
      }
      if (!formGroup) {
        formGroup = this.createDefaultFormGroup(panelContext.property, panelContext.listMetadata.classMetadataItems);
        formGroup.addControl('UI_INDEX', this.formBuilder.control(formArray.length));
        formArray.push(formGroup);
      }
      context.formGroup = formGroup;
      context.path = panelContext.path + '.' + index;
    }
  }

  containsError(context: IPanelContext): boolean {
    if (context) {
      return context.fieldContexts.find((f) => f.control.invalid && f.control.errors != null && 
        f.control.errors["rule-violation"] && f.control.errors["rule-violation"].violationType == 'Error') !== undefined;
    }
    return false;
  }

  private createFieldContext(model: any, metadata: any, property: string, propertyMetadata: any, currentPath: string): IModelContext {
    var context: IModelContext = {
      rootModel: this._model,
      rootModelMetadata: this._metadata,
      model: model,
      modelMetadata: metadata,
      path: currentPath,
      property: property,
      metadata: propertyMetadata,
      displayOrder: propertyMetadata.displayOrder || 0,
      hidden: propertyMetadata.hidden,
    };

    var columns = propertyMetadata.columns || 12;
    propertyMetadata.uiColumnWidth = (100 / 12) * columns;

    if (propertyMetadata.parentProperty) {
      context.parentDropdownContext = {
        entityKey: metadata.propertyMetadataItems[propertyMetadata.parentProperty].entityKey,
        formName: propertyMetadata.parentProperty,
        displayName: metadata.propertyMetadataItems[propertyMetadata.parentProperty].displayName,
      };
    }

    if(propertyMetadata.updateOtherPropertyOnChange){
      context.relatedPropertyContext = {
        entityKey: metadata.propertyMetadataItems[propertyMetadata.updateOtherPropertyOnChange].entityKey,
        formName: propertyMetadata.updateOtherPropertyOnChange,
        displayName: metadata.propertyMetadataItems[propertyMetadata.updateOtherPropertyOnChange].displayName,
      };
    }

    return context;
  }

  private isValueType(metadata: any): boolean {
    var type = metadata.templateType;
    return this.metadataTemplateManager.hasKey(type) || type === 'String' || type === 'Number' || type === 'Date' || type === 'TextArea';
  }

  private isArray(metadata: any): boolean {
    var type = metadata.templateType;
    return type === 'Array';
  }

  private isObject(metadata: any): boolean {
    var type = metadata.templateType;
    return type === 'Object';
  }

  private shouldIgnore(metadata: any): boolean {
    var type = metadata.templateType;
    return type === 'Ignore';
  }

  private GetFormGroupForDeleteOperation(panelContext: IListPanelContext, index: number, item: any): FormGroup {
    var formArray = panelContext.formGroup.get(panelContext.property) as FormArray;
    if (index < 0) {
      var newFormGroup = this.createDefaultFormGroup(panelContext.property, panelContext.listMetadata.classMetadataItems);
      newFormGroup.addControl('UI_INDEX', this.formBuilder.control(formArray.length));
      for (var property in item) {
        if (item.hasOwnProperty(property)) {
          newFormGroup.addControl(property, this.formBuilder.control(''));
        }
      }
      newFormGroup.patchValue(item);
      index = formArray.controls.length;
      formArray.push(newFormGroup);
      this._changeDetector.markForCheck();
    }
    var formGroup = formArray.controls[index] as FormGroup;
    return formGroup;
  }

  getInputErrorMessage(error: any) {
    if (typeof error.message === 'string') {
      return error.message;
    } else return 'Invalid value.';
  }

  autoGenerateValue(fieldContext: IModelContext){
    if (fieldContext.control) {
      var id = 'IFB' + uuid();
      fieldContext.control.patchValue(id);
      fieldContext.control.markAsDirty();
    }
  }

  canAutoGenerateValue(fieldContext: IModelContext) : boolean{
    if(this.valueHasBeenAutoGenerated(fieldContext)){
      return false;
    }

    return (fieldContext.metadata.canAutoGenerateValue && !fieldContext.control.value);
  }

  hasAutoGeneratedValue(modelContext: IModelContext){
    return this.valueHasBeenAutoGenerated(modelContext);
  }

  private valueHasBeenAutoGenerated(modelContext: IModelContext) : boolean {
    if(modelContext.metadata.canAutoGenerateValue && modelContext.control && modelContext.control.value){
      if(modelContext.control.value.substring(0,3) == 'IFB' && modelContext.control.value.length == 39){
        return true;
      }
    }
    return false;
  }

  fieldHasWarningOnly(formGroup: any, modelContext : any) : boolean{
    if(!formGroup.controls[modelContext.property].errors){
      return false;
    }
    var ruleViolations = formGroup.controls[modelContext.property].errors["rule-violation"];
    if (ruleViolations == null) {
      return false;
    }
    if(Array.isArray(ruleViolations)){
      return ruleViolations.every(v => v.violationType == 'Warning');
    }
    else{
      return ruleViolations["violationType"] == 'Warning';
    }
  }

  formatDate(formGroup: any, modelContext: any, event: MatDatepickerInputEvent<Date>) {
    var date = formGroup.controls[modelContext.property].value;
    if(!date.isUTC()){      
      var localDate = new Date(date);
      var newDate = new Date(localDate.getTime() - localDate.getTimezoneOffset() * 60000);
      var utcDate = moment.utc(newDate);
      formGroup.controls[modelContext.property].value = utcDate;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/pillar/workspace/components/metadata-workspace/metadata-workspace.component.ts